/**
 * Templates profissionais de projetos para fallback da IA.
 * Usados quando a API Gemini falha ou como opção direta.
 *
 * Aqui fica só a ESTRUTURA (duração, tipo, dependência, cor, ordem das fases),
 * que não muda com o idioma. O TEXTO vive em `src/i18n/locales/*\/templates.ts`
 * e é lido a cada chamada por `getCurrentCopy()`.
 *
 * Por que `getCurrentCopy()` e não um parâmetro de dicionário: o que sai daqui
 * é semeado no banco do cliente no instante em que o projeto é criado, então
 * tem que estar no idioma escolhido NA CRIAÇÃO — e as três funções exportadas
 * são chamadas de `AIProjectModal` e `aiProjectService`, cujas assinaturas não
 * mudam. O `LocaleProvider` grava o dicionário antes de qualquer filho
 * renderizar.
 */

import { getCurrentCopy } from '../i18n/current';

export interface AIGeneratedPlan {
  project: {
    name: string;
    description: string;
  };
  team: Array<{
    name: string;
    role_title: string;
    avatar_color: string;
  }>;
  phases: Array<{
    name: string;
    color: string;
    tasks: Array<{
      name: string;
      type: 'task' | 'milestone';
      duration: string;
      assigned_role?: string;
      description?: string;
      depends_on_index?: number;
    }>;
  }>;
}

// Cores profissionais para fases
const PHASE_COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];

interface TaskStructure {
  /** Chave da tarefa no dicionário, dentro da fase. Nunca índice de array. */
  key: string;
  type: 'task' | 'milestone';
  /** Formato canônico persistido ('5d', '0d'). Não é texto de idioma. */
  duration: string;
  /**
   * Chave do MEMBRO da equipe (não o cargo por extenso). O `assigned_role` da
   * tarefa sai do `roleTitle` desse membro, no mesmo idioma, então o casamento
   * com `team[].role_title` que `aiProjectService.roleKey()` faz é por
   * construção — não depende de duas strings traduzidas coincidirem.
   */
  role: string;
  /** Índice GLOBAL da tarefa predecessora, contando todas as fases em ordem. */
  depends_on_index?: number;
}

interface PhaseStructure {
  /** Chave da fase no dicionário. */
  key: string;
  color: string;
  tasks: TaskStructure[];
}

interface TemplateStructure {
  team: Array<{ key: string; avatar_color: string }>;
  phases: PhaseStructure[];
}

/**
 * Estrutura dos modelos. A ordem das chaves é a ordem da galeria em
 * `getAllTemplates()`, e as chaves (`software`, `website`...) são passadas pelo
 * `WelcomeModal` e pelo `AIProjectModal`: não mudam com o idioma.
 */
const TEMPLATE_STRUCTURE: Record<string, TemplateStructure> = {
  software: {
    team: [
      { key: 'pm', avatar_color: '#7e3eee' },
      { key: 'po', avatar_color: '#3b82f6' },
      { key: 'ux', avatar_color: '#ec4899' },
      { key: 'frontend', avatar_color: '#22c55e' },
      { key: 'backend', avatar_color: '#f59e0b' },
      { key: 'qa', avatar_color: '#ef4444' },
    ],
    phases: [
      {
        key: 'discovery',
        color: PHASE_COLORS[0],
        tasks: [
          { key: 'requirements', type: 'task', duration: '5d', role: 'po' },
          { key: 'feasibility', type: 'task', duration: '3d', role: 'backend' },
          { key: 'scope', type: 'task', duration: '3d', role: 'po', depends_on_index: 0 },
          { key: 'schedule', type: 'task', duration: '2d', role: 'pm', depends_on_index: 2 },
          { key: 'kickoff', type: 'milestone', duration: '0d', role: 'pm', depends_on_index: 3 },
        ],
      },
      {
        key: 'design',
        color: PHASE_COLORS[1],
        tasks: [
          { key: 'userResearch', type: 'task', duration: '4d', role: 'ux', depends_on_index: 4 },
          { key: 'wireframes', type: 'task', duration: '5d', role: 'ux', depends_on_index: 5 },
          { key: 'visualDesign', type: 'task', duration: '8d', role: 'ux', depends_on_index: 6 },
          { key: 'validation', type: 'task', duration: '2d', role: 'po', depends_on_index: 7 },
          { key: 'approved', type: 'milestone', duration: '0d', role: 'ux', depends_on_index: 8 },
        ],
      },
      {
        key: 'development',
        color: PHASE_COLORS[2],
        tasks: [
          { key: 'setup', type: 'task', duration: '3d', role: 'backend', depends_on_index: 9 },
          { key: 'frontend', type: 'task', duration: '15d', role: 'frontend', depends_on_index: 10 },
          { key: 'backend', type: 'task', duration: '15d', role: 'backend', depends_on_index: 10 },
          { key: 'integration', type: 'task', duration: '5d', role: 'frontend', depends_on_index: 11 },
          { key: 'done', type: 'milestone', duration: '0d', role: 'pm', depends_on_index: 13 },
        ],
      },
      {
        key: 'testing',
        color: PHASE_COLORS[3],
        tasks: [
          { key: 'automatedTests', type: 'task', duration: '5d', role: 'qa', depends_on_index: 14 },
          { key: 'uat', type: 'task', duration: '5d', role: 'po', depends_on_index: 15 },
          { key: 'bugfix', type: 'task', duration: '5d', role: 'frontend', depends_on_index: 16 },
          { key: 'approved', type: 'milestone', duration: '0d', role: 'qa', depends_on_index: 17 },
        ],
      },
      {
        key: 'launch',
        color: PHASE_COLORS[4],
        tasks: [
          { key: 'prodEnv', type: 'task', duration: '2d', role: 'backend', depends_on_index: 18 },
          { key: 'dataMigration', type: 'task', duration: '2d', role: 'backend', depends_on_index: 19 },
          { key: 'deploy', type: 'task', duration: '1d', role: 'backend', depends_on_index: 20 },
          { key: 'training', type: 'task', duration: '3d', role: 'po', depends_on_index: 21 },
          { key: 'goLive', type: 'milestone', duration: '0d', role: 'pm', depends_on_index: 22 },
        ],
      },
    ],
  },

  website: {
    team: [
      { key: 'pm', avatar_color: '#7e3eee' },
      { key: 'designer', avatar_color: '#ec4899' },
      { key: 'developer', avatar_color: '#22c55e' },
      { key: 'writer', avatar_color: '#f59e0b' },
      { key: 'seo', avatar_color: '#3b82f6' },
    ],
    phases: [
      {
        key: 'planning',
        color: PHASE_COLORS[0],
        tasks: [
          { key: 'briefing', type: 'task', duration: '2d', role: 'pm' },
          { key: 'competitors', type: 'task', duration: '2d', role: 'seo' },
          { key: 'sitemap', type: 'task', duration: '2d', role: 'designer', depends_on_index: 0 },
          { key: 'approved', type: 'milestone', duration: '0d', role: 'pm', depends_on_index: 2 },
        ],
      },
      {
        key: 'design',
        color: PHASE_COLORS[1],
        tasks: [
          { key: 'wireframes', type: 'task', duration: '4d', role: 'designer', depends_on_index: 3 },
          { key: 'visualDesign', type: 'task', duration: '6d', role: 'designer', depends_on_index: 4 },
          { key: 'approval', type: 'task', duration: '2d', role: 'pm', depends_on_index: 5 },
          { key: 'done', type: 'milestone', duration: '0d', role: 'designer', depends_on_index: 6 },
        ],
      },
      {
        key: 'content',
        color: PHASE_COLORS[2],
        tasks: [
          { key: 'copywriting', type: 'task', duration: '5d', role: 'writer', depends_on_index: 7 },
          { key: 'keywords', type: 'task', duration: '3d', role: 'seo', depends_on_index: 7 },
          { key: 'media', type: 'task', duration: '4d', role: 'designer', depends_on_index: 7 },
          { key: 'ready', type: 'milestone', duration: '0d', role: 'writer', depends_on_index: 8 },
        ],
      },
      {
        key: 'development',
        color: PHASE_COLORS[3],
        tasks: [
          { key: 'coding', type: 'task', duration: '10d', role: 'developer', depends_on_index: 11 },
          { key: 'contentIntegration', type: 'task', duration: '3d', role: 'developer', depends_on_index: 12 },
          { key: 'seoOnPage', type: 'task', duration: '3d', role: 'seo', depends_on_index: 13 },
          { key: 'analytics', type: 'task', duration: '1d', role: 'seo', depends_on_index: 14 },
          { key: 'built', type: 'milestone', duration: '0d', role: 'developer', depends_on_index: 15 },
        ],
      },
      {
        key: 'launch',
        color: PHASE_COLORS[4],
        tasks: [
          { key: 'crossBrowser', type: 'task', duration: '3d', role: 'developer', depends_on_index: 16 },
          { key: 'hosting', type: 'task', duration: '1d', role: 'developer', depends_on_index: 17 },
          { key: 'publish', type: 'task', duration: '1d', role: 'developer', depends_on_index: 18 },
          { key: 'live', type: 'milestone', duration: '0d', role: 'pm', depends_on_index: 19 },
        ],
      },
    ],
  },

  marketing: {
    team: [
      { key: 'coordinator', avatar_color: '#7e3eee' },
      { key: 'socialMedia', avatar_color: '#ec4899' },
      { key: 'designer', avatar_color: '#22c55e' },
      { key: 'copywriter', avatar_color: '#f59e0b' },
      { key: 'mediaAnalyst', avatar_color: '#3b82f6' },
    ],
    phases: [
      {
        key: 'strategy',
        color: PHASE_COLORS[0],
        tasks: [
          { key: 'goals', type: 'task', duration: '2d', role: 'coordinator' },
          { key: 'audience', type: 'task', duration: '3d', role: 'coordinator' },
          { key: 'channels', type: 'task', duration: '2d', role: 'mediaAnalyst', depends_on_index: 1 },
          { key: 'approved', type: 'milestone', duration: '0d', role: 'coordinator', depends_on_index: 2 },
        ],
      },
      {
        key: 'content',
        color: PHASE_COLORS[1],
        tasks: [
          { key: 'calendar', type: 'task', duration: '2d', role: 'socialMedia', depends_on_index: 3 },
          { key: 'copy', type: 'task', duration: '5d', role: 'copywriter', depends_on_index: 4 },
          { key: 'visuals', type: 'task', duration: '5d', role: 'designer', depends_on_index: 4 },
          { key: 'review', type: 'task', duration: '2d', role: 'coordinator', depends_on_index: 5 },
          { key: 'approved', type: 'milestone', duration: '0d', role: 'coordinator', depends_on_index: 7 },
        ],
      },
      {
        key: 'setup',
        color: PHASE_COLORS[2],
        tasks: [
          { key: 'paidMedia', type: 'task', duration: '3d', role: 'mediaAnalyst', depends_on_index: 8 },
          { key: 'email', type: 'task', duration: '2d', role: 'socialMedia', depends_on_index: 8 },
          { key: 'tracking', type: 'task', duration: '1d', role: 'mediaAnalyst', depends_on_index: 9 },
          { key: 'launch', type: 'task', duration: '1d', role: 'coordinator', depends_on_index: 11 },
          { key: 'live', type: 'milestone', duration: '0d', role: 'coordinator', depends_on_index: 12 },
        ],
      },
      {
        key: 'monitoring',
        color: PHASE_COLORS[3],
        tasks: [
          { key: 'dailyMetrics', type: 'task', duration: '10d', role: 'mediaAnalyst', depends_on_index: 13 },
          { key: 'socialManagement', type: 'task', duration: '10d', role: 'socialMedia', depends_on_index: 13 },
          { key: 'optimization', type: 'task', duration: '5d', role: 'mediaAnalyst', depends_on_index: 14 },
          { key: 'finalReport', type: 'task', duration: '3d', role: 'coordinator', depends_on_index: 16 },
          { key: 'closed', type: 'milestone', duration: '0d', role: 'coordinator', depends_on_index: 17 },
        ],
      },
    ],
  },

  evento: {
    team: [
      { key: 'coordinator', avatar_color: '#7e3eee' },
      { key: 'communications', avatar_color: '#ec4899' },
      { key: 'logistics', avatar_color: '#22c55e' },
      { key: 'designer', avatar_color: '#f59e0b' },
    ],
    phases: [
      {
        key: 'planning',
        color: PHASE_COLORS[0],
        tasks: [
          { key: 'concept', type: 'task', duration: '3d', role: 'coordinator' },
          { key: 'budget', type: 'task', duration: '2d', role: 'coordinator', depends_on_index: 0 },
          { key: 'venue', type: 'task', duration: '5d', role: 'logistics', depends_on_index: 1 },
          { key: 'agenda', type: 'task', duration: '2d', role: 'coordinator', depends_on_index: 2 },
          { key: 'approved', type: 'milestone', duration: '0d', role: 'coordinator', depends_on_index: 3 },
        ],
      },
      {
        key: 'vendors',
        color: PHASE_COLORS[1],
        tasks: [
          { key: 'hiring', type: 'task', duration: '5d', role: 'logistics', depends_on_index: 4 },
          { key: 'speakers', type: 'task', duration: '5d', role: 'coordinator', depends_on_index: 4 },
          { key: 'materials', type: 'task', duration: '3d', role: 'logistics', depends_on_index: 5 },
          { key: 'confirmed', type: 'milestone', duration: '0d', role: 'logistics', depends_on_index: 7 },
        ],
      },
      {
        key: 'communication',
        color: PHASE_COLORS[2],
        tasks: [
          { key: 'branding', type: 'task', duration: '4d', role: 'designer', depends_on_index: 4 },
          { key: 'landingPage', type: 'task', duration: '3d', role: 'communications', depends_on_index: 9 },
          { key: 'promotion', type: 'task', duration: '10d', role: 'communications', depends_on_index: 10 },
          { key: 'registrations', type: 'task', duration: '5d', role: 'communications', depends_on_index: 11 },
          { key: 'closed', type: 'milestone', duration: '0d', role: 'coordinator', depends_on_index: 12 },
        ],
      },
      {
        key: 'execution',
        color: PHASE_COLORS[3],
        tasks: [
          { key: 'setup', type: 'task', duration: '1d', role: 'logistics', depends_on_index: 13 },
          { key: 'eventDay', type: 'task', duration: '1d', role: 'coordinator', depends_on_index: 14 },
          { key: 'teardown', type: 'task', duration: '1d', role: 'logistics', depends_on_index: 15 },
          { key: 'survey', type: 'task', duration: '3d', role: 'communications', depends_on_index: 15 },
          { key: 'finalReport', type: 'task', duration: '3d', role: 'coordinator', depends_on_index: 17 },
          { key: 'done', type: 'milestone', duration: '0d', role: 'coordinator', depends_on_index: 18 },
        ],
      },
    ],
  },

  implantacao: {
    team: [
      { key: 'pm', avatar_color: '#7e3eee' },
      { key: 'functional', avatar_color: '#3b82f6' },
      { key: 'technical', avatar_color: '#22c55e' },
      { key: 'dataAnalyst', avatar_color: '#f59e0b' },
      { key: 'keyUser', avatar_color: '#ec4899' },
    ],
    phases: [
      {
        key: 'analysis',
        color: PHASE_COLORS[0],
        tasks: [
          { key: 'asIs', type: 'task', duration: '5d', role: 'functional' },
          { key: 'requirements', type: 'task', duration: '5d', role: 'functional', depends_on_index: 0 },
          { key: 'toBe', type: 'task', duration: '4d', role: 'functional', depends_on_index: 1 },
          { key: 'approved', type: 'milestone', duration: '0d', role: 'pm', depends_on_index: 2 },
        ],
      },
      {
        key: 'configuration',
        color: PHASE_COLORS[1],
        tasks: [
          { key: 'install', type: 'task', duration: '3d', role: 'technical', depends_on_index: 3 },
          { key: 'parameterization', type: 'task', duration: '8d', role: 'functional', depends_on_index: 4 },
          { key: 'customizations', type: 'task', duration: '10d', role: 'technical', depends_on_index: 5 },
          { key: 'permissions', type: 'task', duration: '3d', role: 'technical', depends_on_index: 5 },
          { key: 'configured', type: 'milestone', duration: '0d', role: 'technical', depends_on_index: 6 },
        ],
      },
      {
        key: 'migration',
        color: PHASE_COLORS[2],
        tasks: [
          { key: 'legacyMapping', type: 'task', duration: '4d', role: 'dataAnalyst', depends_on_index: 8 },
          { key: 'cleanup', type: 'task', duration: '5d', role: 'dataAnalyst', depends_on_index: 9 },
          { key: 'load', type: 'task', duration: '3d', role: 'dataAnalyst', depends_on_index: 10 },
          { key: 'validation', type: 'task', duration: '3d', role: 'keyUser', depends_on_index: 11 },
          { key: 'validated', type: 'milestone', duration: '0d', role: 'dataAnalyst', depends_on_index: 12 },
        ],
      },
      {
        key: 'training',
        color: PHASE_COLORS[3],
        tasks: [
          { key: 'integratedTests', type: 'task', duration: '5d', role: 'keyUser', depends_on_index: 13 },
          { key: 'fixes', type: 'task', duration: '4d', role: 'technical', depends_on_index: 14 },
          { key: 'material', type: 'task', duration: '4d', role: 'functional', depends_on_index: 14 },
          { key: 'keyUsers', type: 'task', duration: '5d', role: 'functional', depends_on_index: 16 },
          { key: 'endUsers', type: 'task', duration: '5d', role: 'keyUser', depends_on_index: 17 },
          { key: 'approved', type: 'milestone', duration: '0d', role: 'pm', depends_on_index: 18 },
        ],
      },
      {
        key: 'goLive',
        color: PHASE_COLORS[4],
        tasks: [
          { key: 'preparation', type: 'task', duration: '2d', role: 'pm', depends_on_index: 19 },
          { key: 'cutover', type: 'task', duration: '1d', role: 'technical', depends_on_index: 20 },
          { key: 'hypercare', type: 'task', duration: '10d', role: 'functional', depends_on_index: 21 },
          { key: 'report', type: 'task', duration: '3d', role: 'pm', depends_on_index: 22 },
          { key: 'closed', type: 'milestone', duration: '0d', role: 'pm', depends_on_index: 23 },
        ],
      },
    ],
  },
};

/**
 * Forma do texto de UM modelo no dicionário.
 *
 * Cada modelo tem chaves NOMEADAS próprias (membro, fase, tarefa), então o tipo
 * de `templates.projects.software` é diferente do de `...website` e não dá para
 * indexar por chave dinâmica sem esta forma comum. O cast é estreito e
 * `src/data/__tests__/projectTemplates.test.ts` confere, nos três idiomas, que
 * toda chave da estrutura existe no dicionário e chega com texto.
 */
interface TemplateText {
  name: string;
  description: string;
  team: Record<string, { name: string; roleTitle: string }>;
  phases: Record<string, { name: string; tasks: Record<string, { name: string; description?: string }> }>;
}

function templateText(key: string): TemplateText {
  const projects = getCurrentCopy().templates.projects as unknown as Record<string, TemplateText>;
  return projects[key];
}

/** Monta o plano do modelo `key` com o texto do idioma atual. */
function buildTemplate(key: string): AIGeneratedPlan {
  const structure = TEMPLATE_STRUCTURE[key];
  const text = templateText(key);

  return {
    project: { name: text.name, description: text.description },
    team: structure.team.map(member => ({
      name: text.team[member.key].name,
      role_title: text.team[member.key].roleTitle,
      avatar_color: member.avatar_color,
    })),
    phases: structure.phases.map(phase => {
      const phaseText = text.phases[phase.key];
      return {
        name: phaseText.name,
        color: phase.color,
        tasks: phase.tasks.map(task => {
          const taskText = phaseText.tasks[task.key];
          const built: AIGeneratedPlan['phases'][number]['tasks'][number] = {
            name: taskText.name,
            type: task.type,
            duration: task.duration,
            // Mesma fonte que o `role_title` do membro: é isso que faz o
            // casamento de `aiProjectService.roleKey()` sobreviver à tradução.
            assigned_role: text.team[task.role].roleTitle,
          };
          if (taskText.description) built.description = taskText.description;
          if (task.depends_on_index !== undefined) built.depends_on_index = task.depends_on_index;
          return built;
        }),
      };
    }),
  };
}

/**
 * Encontra o template mais adequado baseado na descrição do usuário.
 * Retorna uma cópia profunda do template com o nome personalizado.
 */
export function findBestTemplate(description: string): AIGeneratedPlan {
  const normalizedDesc = description.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  let bestMatch = 'software'; // Default
  let bestScore = 0;

  // Palavras-chave do idioma atual: quem descreve o projeto escreve no idioma
  // em que está usando o produto.
  for (const [templateKey, keywords] of Object.entries(getCurrentCopy().templates.keywords)) {
    if (!TEMPLATE_STRUCTURE[templateKey]) continue;
    let score = 0;
    for (const keyword of keywords) {
      const normalizedKeyword = keyword.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      if (normalizedDesc.includes(normalizedKeyword)) {
        score += keyword.length; // Longer matches score higher
      }
    }
    if (score > bestScore) {
      bestScore = score;
      bestMatch = templateKey;
    }
  }

  // Cada chamada monta objetos novos a partir do dicionário: não há estado
  // compartilhado para clonar.
  const template = buildTemplate(bestMatch);

  // Customize project name with user's description if it's short enough
  if (description.length <= 80) {
    template.project.name = description.charAt(0).toUpperCase() + description.slice(1);
  }

  return template;
}

/**
 * Retorna todos os templates disponíveis com metadados enriquecidos
 */
export function getAllTemplates(): Array<{
  key: string;
  name: string;
  description: string;
  phaseCount: number;
  taskCount: number;
  teamCount: number;
}> {
  return Object.keys(TEMPLATE_STRUCTURE).map(key => {
    const structure = TEMPLATE_STRUCTURE[key];
    const text = templateText(key);
    return {
      key,
      name: text.name,
      description: text.description,
      phaseCount: structure.phases.length,
      taskCount: structure.phases.reduce((sum, p) => sum + p.tasks.length, 0),
      teamCount: structure.team.length,
    };
  });
}

/**
 * Retorna um template específico por key
 */
export function getTemplateByKey(key: string): AIGeneratedPlan | null {
  if (!TEMPLATE_STRUCTURE[key]) return null;
  return buildTemplate(key);
}
